import { create } from 'zustand';

import Features from '@src/features.config';

import { defaultCardDetails, payerAuthStatus } from '@helper/constants';
import DebugConsole from '@helper/functions/console';
import {
  getMaskedValueCCFromJWT,
  getMicroformErrorMessage,
  getPayerAuthEnrolmentErrorMessage,
  getTransientTokenFromJWT,
  getValidatePayerAuthErrorMessage,
} from '@helper/functions/flex';
import {
  postGenerateReceipt,
  postGenerateReprintReceipt,
  sendRating,
  sendReceiptEmail,
  sendReprintReceiptEmail,
} from '@helper/service/invoice';
import { validateTerminalPayment } from '@helper/service/invoice';
import * as PaymentService from '@helper/service/payment';
import * as RecaptchaService from '@helper/service/recaptcha';

import useNavStore from '@store/nav';

import { useDialogStore } from './dialog';
import { usePaymentBpointStore } from './payment-bpoint';

export const recognisedPaymentTypes = ['sd', 'enable', 'donation', 'misc', 'pbrc', ''];
export const recognisedPaymentMethods = ['creditCard', 'cash', 'cheque', 'payto'];

const CREDITOR_INFO = {
  party_type: 'ORGN',
  party_name: 'NSW Health',
  ultimate_party_name: 'NSW Health',
  account_identification_type_code: 'BBAN',
  account_identification: process.env.REACT_APP_CREDITOR_ACCOUNT_ID,
  party_reference: process.env.REACT_APP_PARTY_REFERENCE,
};

export const useActiveTransactionStore = create((set, get) => ({
  activeTx: {},
  activeStep: 'step1',
  setActiveStep: (step) => set({ activeStep: step }),

  updateActiveTx: (entries) => {
    set({
      activeTx: { ...get().activeTx, ...entries },
    });
  },

  paymentMethod: '',
  setPaymentMethod: (newMethod) => {
    set({
      paymentMethod: newMethod,
      activeTx: { ...get().activeTx, paymentMethod: newMethod },
    });
  },

  paymentGateway: '',
  setPaymentGateway: (gateway) => {
    set({
      paymentGateway: gateway,
    });
  },

  setPaymentAmount: (amount) => {
    set({
      activeTx: { ...get().activeTx, amount: parseFloat(amount).toFixed(2) },
    });
  },

  setCashData: (data) => {
    set({
      activeTx: {
        ...get().activeTx,
        cash: {
          //paidBy: data.paidBy,
          amountChange: data.amountChange,
          amountTendered: data.amountTendered,
          amountPayable: data.amountPayable,
        },
      },
    });
  },

  setChequeData: (data) => {
    set({
      activeTx: {
        ...get().activeTx,
        cheque: {
          drawer: data.drawer,
          chequeNumber: data.chequeNumber,
          accountNumber: data.accountNumber,
          bsb: data.bsb,
        },
      },
    });
  },

  setTerminalData: (data) => {
    console.log(data);
    set({
      activeTx: {
        ...get().activeTx,
        terminal: {
          paymentDate: data.paymentDate,
          paymentTime: data.paymentTime,
          terminalID: data.terminalID,
          merchantID: data.merchantID,
          sequenceNumber: data.sequenceNumber,
          comments: data.comments,
          bankIdentifier: data.bankIdentifier ? data.bankIdentifier : '',
        },
      },
    });
  },

  setTerminalDataBulk: (data) => {
    set({
      activeTx: {
        ...get().activeTx,
        terminalBulk: {
          paymentDateFrom: data.paymentDateFrom,
          paymentDateTo: data.paymentDateTo,
          terminalID: data.terminalID,
          merchantID: data.merchantID,
          comments: data.comments,
          bankIdentifier: data.bankIdentifier ? data.bankIdentifier : '',
        },
      },
    });
  },

  setPaytoData: (data) => {
    set({
      activeTx: {
        ...get().activeTx,
        payto: {
          paymentFrequency: data.paymentFrequency,
          amount: parseFloat(data.amount).toFixed(2),
          customerPayId: data.customerPayId,
          bsb: data.bsb,
          accountNumber: data.accountNumber,
          payToType: data.payToType,
          aliasType: data.aliasType,
        },
      },
    });
  },

  resetTransaction: () => {
    set({
      activeTx: {},
      activeStep: 'step1',
      paymentMethod: '',
      paymentGateway: '',
      onSuccessCallback: undefined,
    });
  },

  /**
   * Initialise a transaction
   */
  initTransaction: (
    {
      paymentType = 'misc',
      merchantReference = '',
      referencePrefix = '',
      amount = '0.00',
      allowCustomAmount = false,
      healthEntityCode = '',
      payerAuthCode,
      billingFirstName = '',
      billingLastName = '',
      billingCustomerFullName = '',
      billingEmail = '',
      formData,
      confirmInfoFields = [],
    },
    onSuccessCallback,
  ) => {
    let data = {
      paymentType,
      merchantReference,
      referencePrefix,
      payerAuthCode,
      amount: parseFloat(amount).toFixed(2),
      allowCustomAmount,
      healthEntityCode,
      billingEmail,
      billingFirstName,
      billingLastName,
      billingCustomerFullName,
      formData: { ...formData },
      confirmInfoFields: [...confirmInfoFields],
    };

    set({
      activeTx: data,
      onSuccessCallback,
    });

    useDialogStore.getState().handlePaymentDialog(true);
  },

  onSuccessCallback: undefined,

  handlePaymentSuccess: async () => {
    usePaymentStore.getState().resetValidatePayerAuthData();
    if (get().onSuccessCallback && typeof get().onSuccessCallback === 'function') {
      get().onSuccessCallback();
      set({ onSuccessCallback: undefined });
    }
  },
}));

export const usePaymentStore = create((set, get) => ({
  // Card details
  maskedValueCC: '',
  cardDetails: {},
  setCardDetails: (values) => {
    return set((state) => ({
      cardDetails: { ...state.cardDetails, ...values },
    }));
  },

  cardType: '',
  setCardType: (values) => {
    return set({ cardType: values });
  },

  recaptchaToken: '',
  setRecaptchaToken: (token) => {
    return set({ recaptchaToken: token });
  },

  // Generate microform capture context
  captureContext: undefined,
  generateFlexCaptureContext: async (callback) => {
    const activeTx = useActiveTransactionStore.getState().activeTx;
    console.log('Print active transaction: ', JSON.stringify(activeTx));

    const healthEntityCode = activeTx.healthEntityCode;
    const paymentType = activeTx.paymentType;

    let captureContext;
    console.log('Print payment type: ', paymentType);

    const isInternational = useNavStore.getState().isInternational;
    if (isInternational) {
      captureContext = await PaymentService.getGenerateCaptureContext(
        healthEntityCode,
        paymentType,
        true,
      );
    } else {
      captureContext = await PaymentService.getGenerateCaptureContext(
        healthEntityCode,
        paymentType,
      );
    }

    if (!captureContext) return callback(false);

    set({
      captureContext,
    });
    callback(captureContext);
  },

  // Flex transient toke
  transientTokenJWT: '',
  transientTokenError: {},
  generateFlexTransientToken: async (microform, recaptchaToken, isRecaptchaEnabled, callback) => {
    let recaptchaResponse = null;
    if (isRecaptchaEnabled) {
      const isInternational = useNavStore.getState().isInternational;
      if (isInternational) {
        recaptchaResponse = await RecaptchaService.postValidateRecaptcha(recaptchaToken, true);
      } else {
        recaptchaResponse = await RecaptchaService.postValidateRecaptcha(recaptchaToken);
      }
    }
    if (isRecaptchaEnabled && !recaptchaResponse?.success) return callback(false);

    const { cardDetails } = get();

    const { expiry, cardType } = cardDetails;
    const [expiryMonth, expiryYear] = expiry.split('/');

    const currentYearStartingYY = new Date().getFullYear().toString().slice(0, 2);
    const requestOptions = {
      type: cardType,
      expirationMonth: expiryMonth,
      expirationYear: `${currentYearStartingYY}${expiryYear}`,
    };

    try {
      microform.createToken(requestOptions, (error, token) => {
        if (error) {
          const { messageLocation } = getMicroformErrorMessage(error);

          set({
            transientTokenError: messageLocation,
            showPaymentFormErrors: true,
          });
          return callback(false);
        }

        set({ transientTokenJWT: token });
        set({ maskedValueCC: getMaskedValueCCFromJWT(token) });

        callback(true);
      });
    } catch (err) {
      DebugConsole.error('Could not request flex microform transient token');
      DebugConsole.error(err);

      return callback(false);
    }
  },

  // Process payment
  isPaymentProcessing: false,
  paymentRes: null,
  paymentError: null,
  showPaymentFormErrors: false,
  processPayment: async (user, callback) => {
    set({ isPaymentProcessing: true });

    const activeTx = useActiveTransactionStore.getState().activeTx;
    const paymentMethod = useActiveTransactionStore.getState().paymentMethod;
    const paymentGateway = useActiveTransactionStore.getState().paymentGateway;

    if (['cash', 'cheque', 'terminal'].includes(paymentMethod)) {
      // Payment service payload
      const payload = {
        reference: activeTx.merchantReference || activeTx.paymentType,
        referencePrefix: activeTx.referencePrefix,
        paymentType: activeTx.paymentType,
        paymentMethod: paymentMethod,
        amount: activeTx.amount,
        jwtToken: 'CASHIER_PAYMENT',
        paConsumerAuthInformation: {},
        activeInvoice: { ...activeTx, paymentMethod: paymentMethod },
        healthEntityCode: activeTx.healthEntityCode,
        userName: user.isLoggedIn && user.Roles.includes('cashier') ? user.UserName : '',
        userFullName: user.isLoggedIn && user.Roles.includes('cashier') ? user.UserFullName : '',
        userEmail: user.isLoggedIn && user.Roles.includes('cashier') ? user.UserEmail : '',
      };

      const paymentType = activeTx.paymentType;
      let paymentRes;
      console.log('Print payment type: ', paymentType);
      const isInternational = useNavStore.getState().isInternational;
      if (isInternational) {
        paymentRes = await PaymentService.postFlexProcessPayment(payload, true);
      } else {
        paymentRes = await PaymentService.postFlexProcessPayment(payload);
      }

      if (!paymentRes) return set({ paymentError: 'Server failed to process the payment' });

      const { paymentData, invoiceData } = paymentRes;

      set({ isPaymentProcessing: false });
      set({ isRatingSubmitted: false });

      // handle payment errors and return
      if (!paymentData) return set({ paymentError: 'Server failed to process the payment' });
      if ('errorInformation' in paymentData)
        return set({ paymentError: paymentData?.errorInformation?.message });

      // reset payment state
      set({ paymentRes, cardDetails: { ...defaultCardDetails }, paymentError: null });
    }

    /**
     * BPOINT payment
     */
    if (
      Features.paymentMethods.bpoint &&
      paymentMethod === 'creditCard' &&
      paymentGateway === 'bpoint'
    ) {
      const { cardDetails } = get();

      const amount = parseFloat(cardDetails.amount).toFixed(2);

      const paymentRes = await usePaymentBpointStore.getState().processPayment({
        reference: activeTx.merchantReference || activeTx.paymentType,
        referencePrefix: activeTx.referencePrefix,
        paymentType: activeTx.paymentType,
        paymentMethod: 'creditCard',
        amount,
        activeInvoice: activeTx,
        healthEntityCode: activeTx.healthEntityCode,
        userName: user.isLoggedIn && user.Roles.includes('cashier') ? user.UserName : '',
        userFullName: user.isLoggedIn && user.Roles.includes('cashier') ? user.UserFullName : '',
        userEmail: user.isLoggedIn && user.Roles.includes('cashier') ? user.UserEmail : '',
      });

      if (!paymentRes) {
        DebugConsole.error(`BPOINT payment res:`, paymentRes);

        set({
          paymentError: 'Server failed to process the payment',
          isPaymentProcessing: false,
        });
        return;
      }

      if (paymentRes?.status === 'ERROR') {
        DebugConsole.error(`BPOINT payment res:`, paymentRes);

        set({
          paymentError: paymentRes?.message || 'Server failed to process the payment',
          isPaymentProcessing: false,
        });
        return;
      }

      DebugConsole.log(`BPOINT payment res:`, paymentRes);

      // Reset BPOINT payment state
      usePaymentBpointStore.getState().reset();
      set({
        paymentRes: {
          paymentData: { bpoint: paymentRes.paymentData.txn },
          invoiceData: paymentRes.invoiceData,
        },
        paymentError: null,
      });

      set({ isPaymentProcessing: false });
      set({ isRatingSubmitted: false });
    }

    /**
     * CyberSource payment
     */
    if (paymentMethod === 'creditCard' && (paymentGateway === 'cybersource' || !paymentGateway)) {
      const { transientTokenJWT, cardDetails, enrollData, validatePayerAuthData } = get();

      const amount = parseFloat(cardDetails.amount).toFixed(2);
      const paConsumerAuthInformation = validatePayerAuthData
        ? validatePayerAuthData.consumerAuthenticationInformation
        : enrollData.consumerAuthenticationInformation;

      // Payment service payload
      const payload = {
        reference: activeTx.merchantReference || activeTx.paymentType,
        referencePrefix: activeTx.referencePrefix,
        paymentType: activeTx.paymentType,
        paymentMethod: 'creditCard',
        amount,
        jwtToken: transientTokenJWT,
        paConsumerAuthInformation,
        activeInvoice: activeTx,
        healthEntityCode: activeTx.healthEntityCode,
        userName: user.isLoggedIn && user.Roles.includes('cashier') ? user.UserName : '',
        userFullName: user.isLoggedIn && user.Roles.includes('cashier') ? user.UserFullName : '',
        userEmail: user.isLoggedIn && user.Roles.includes('cashier') ? user.UserEmail : '',
      };

      const paymentType = activeTx.paymentType;

      let paymentRes;
      console.log('Print payment type: ', paymentType);

      const isInternational = useNavStore.getState().isInternational;
      if (isInternational) {
        paymentRes = await PaymentService.postFlexProcessPayment(payload, true);
      } else {
        paymentRes = await PaymentService.postFlexProcessPayment(payload);
      }

      if (!paymentRes)
        return set({
          paymentError: 'Server failed to process the payment',
          isPaymentProcessing: false,
        });

      const { paymentData, invoiceData } = paymentRes;

      set({ isPaymentProcessing: false });
      set({ isRatingSubmitted: false });

      // handle payment errors and return
      if (!paymentData) return set({ paymentError: 'Server failed to process the payment' });
      if ('errorInformation' in paymentData)
        return set({ paymentError: paymentData?.errorInformation?.message });

      // reset payment state
      set({ paymentRes, cardDetails: { ...defaultCardDetails }, paymentError: null });
    }

    // trigger any success callback
    if (callback) {
      callback();
    }
  },
  recallMandatePayTo: async (mandateId, uuid, callback) => {
    set({ isPaymentProcessing: false });
    let paytoRes;
    const activeTx = useActiveTransactionStore.getState().activeTx;
    try {
      let getMandateInfo;
      try {
        console.log('activeTx in recallMandatePayTo:', activeTx);
        const healthEntityCode =
          activeTx.healthEntityCode || activeTx.formData?.healthEntityCode || 'DEFAULT';
        console.log('Using healthEntityCode:', healthEntityCode);
        getMandateInfo = await PaymentService.paytoRequestMandate(uuid, healthEntityCode);
      } catch (e) {
        set({ paymentError: e.message, isPaymentProcessing: false });
        return;
      }
      console.log(getMandateInfo.data.mps_user_id);
      console.log(getMandateInfo.data.request_id);
      console.log(getMandateInfo.data.mandate_webhook_response.mandate_identification);

      if (!getMandateInfo) return set({ paymentError: 'Server failed to get mandate information' });
      const payload = {
        mps_user_id: getMandateInfo.data.mps_user_id,
        request_id: getMandateInfo.data.request_id,
        mandate_identification: getMandateInfo.data.mandate_webhook_response.mandate_identification,
      };
      console.log(getMandateInfo.data)
      const healthEntityCode = activeTx.healthEntityCode || activeTx.formData?.healthEntityCode;
      paytoRes = await PaymentService.paytoRecallMandate(
        getMandateInfo.data.mandate_webhook_response.action_identification,
        payload,
        healthEntityCode,
      );
    } catch (e) {
      set({ paymentError: e.message, isPaymentProcessing: false });
      return;
    }
    console.log(paytoRes);

    if (!paytoRes) return set({ paymentError: 'Server failed to process the payment' });

    set({ isRatingSubmitted: false });
    // reset payment state
    set({ paymentRes: null, paymentError: null });

    // trigger any success callback
    if (callback) {
      callback();
    }

    return paytoRes;
  },
  savePayToPayment: async (user, e2eId, callback) => {
    set({ isPaymentProcessing: true });

    const activeTx = useActiveTransactionStore.getState().activeTx;
    const paymentMethod = useActiveTransactionStore.getState().paymentMethod;

    // Payment service payload
    const payload = {
      reference: activeTx.merchantReference || activeTx.paymentType,
      referencePrefix: activeTx.referencePrefix,
      paymentType: activeTx.paymentType,
      e2eId: e2eId,
      paymentMethod: paymentMethod,
      amount: activeTx.amount,
      jwtToken: 'CASHIER_PAYMENT',
      paConsumerAuthInformation: {},
      activeInvoice: { ...activeTx, paymentMethod: paymentMethod },
      healthEntityCode: activeTx.healthEntityCode,
      userName: user.isLoggedIn && user.Roles.includes('cashier') ? user.UserName : '',
      userFullName: user.isLoggedIn && user.Roles.includes('cashier') ? user.UserFullName : '',
      userEmail: user.isLoggedIn && user.Roles.includes('cashier') ? user.UserEmail : '',
    };

    const paymentType = activeTx.paymentType;
    let paymentRes;
    console.log('Print payment type: ', paymentType);
    const isInternational = useNavStore.getState().isInternational;
    if (isInternational) {
      paymentRes = await PaymentService.postPayToProcessPayment(payload, true);
    } else {
      paymentRes = await PaymentService.postPayToProcessPayment(payload);
    }

    if (!paymentRes) return set({ paymentError: 'Server failed to process the payment' });

    const { paymentData, invoiceData } = paymentRes;

    set({ isPaymentProcessing: false });
    set({ isRatingSubmitted: false });

    // handle payment errors and return
    if (!paymentData) return set({ paymentError: 'Server failed to process the payment' });
    if ('errorInformation' in paymentData)
      return set({ paymentError: paymentData?.errorInformation?.message });

    // reset payment state
    set({ paymentRes, cardDetails: { ...defaultCardDetails }, paymentError: null });

    // trigger any success callback
    if (callback) {
      callback();
    }

    return;
  },
  createMandatePayto: async (uuid, callback) => {
    set({ isPaymentProcessing: true });
    const activeTx = useActiveTransactionStore.getState().activeTx;
    console.log('Print Active Transaction: ');
    console.log(activeTx);

    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    const paytoData = activeTx.payto;
    console.log('PaytoData in createMandatePayto:', paytoData);

    const getAccountAliasTypeCode = (customerPayId, aliasType) => {
      if (!customerPayId || typeof customerPayId !== 'string') {
        console.warn('Invalid customerPayId');
        return null; // Return null for invalid input
      }
      console.log('ALIAS');
      console.log(aliasType);

      // Regular expression to detect email
      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerPayId);

      if (isEmail || aliasType === 'EMAL') {
        console.log('Detected email. Returning account_alias_type_code: EMAL.');
        return 'EMAL'; // Return "EMAL" for email
      }

      // Regular expression to detect phone number in +61- format
      const isPhoneNumber = /^\+61-\d{8,9}$/.test(customerPayId);

      if (isPhoneNumber || aliasType === 'TELI') {
        console.log('Detected phone number. Returning account_alias_type_code: TELI.');
        return 'TELI'; // Return "TELI" for phone number
      }

      if (aliasType === 'AUBN') {
        return 'AUBN';
      }

      if (aliasType === 'ORGN') {
        return 'ORGN';
      }

      console.warn('Unrecognized customerPayId format. Returning null.');
      return null; // Return null for unrecognized formats
    };

    // Handle BSB & Account Number selection
    let accountIdentificationTypeCode = 'ALIA';
    let accountIdentification = '';
    let accountAliasTypeCode = null;
    let accountAliasIdentification = '';
    let debtor_information = {};

    const name = activeTx.formData?.isAnonymous
      ? 'Anonymous'
      : activeTx.formData?.donorDetails?.fullName;
    if (paytoData && paytoData.payToType === 'bsb') {
      // For BSB & Account Number
      accountIdentificationTypeCode = 'BBAN';
      accountIdentification = paytoData.bsb + paytoData.accountNumber; // Join with no space

      debtor_information = {
        party_type: 'PERS',
        party_name: name,
        ultimate_party_name: name,
        account_identification_type_code: accountIdentificationTypeCode,
        account_alias_type_code: accountAliasTypeCode,
        account_identification: accountIdentification,
        party_reference: 'NA',
      };
    } else if (paytoData && paytoData.payToType === 'payid') {
      // For PayID
      accountIdentificationTypeCode = 'ALIA';
      accountIdentification = paytoData.customerPayId;
      accountAliasTypeCode = getAccountAliasTypeCode(paytoData.customerPayId, paytoData.aliasType);

      debtor_information = {
        party_type: 'PERS',
        party_name: name,
        ultimate_party_name: name,
        account_identification_type_code: accountIdentificationTypeCode,
        account_alias_type_code: accountAliasTypeCode,
        account_alias_identification: accountIdentification,
        party_reference: 'NA',
      };
    }

    // Payment service payload
    const payload = {
      healthEntityCode: activeTx.healthEntityCode || 'INVOICE',
      hospitalFacility: activeTx.formData?.hospitalFacility || 'INVOICE',
      receivableActivityDisplay: activeTx.formData?.receivableActivityDisplay || 'INVOICE',
      paymentType: activeTx.paymentType,
      mps_user_id: process.env.REACT_APP_MPS_USER_ID,
      request_id: uuid,
      initiation_request_identification: `ONEOFF-${new Date().toISOString().split('T')[0]}`,
      mandate_type: 'DDTP',
      establishment_scheme: 'AUPM',
      mandate_purpose_code: 'RETL',
      description: 'One-off payment for donation',
      validity_start_date: today.toISOString().split('T')[0], // Format as YYYY-MM-DD
      validity_end_date: today.toISOString().split('T')[0], // Format as YYYY-MM-DD
      automatic_extension_indicator: false,
      transfer_arrangement: 'ONEOFF',
      debtor_information: debtor_information,
      creditor_information: {
        party_type: CREDITOR_INFO.party_type,
        party_name: CREDITOR_INFO.party_name,
        ultimate_party_name: CREDITOR_INFO.ultimate_party_name,
        account_identification_type_code: CREDITOR_INFO.account_identification_type_code,
        account_identification: CREDITOR_INFO.account_identification,
        party_reference: CREDITOR_INFO.party_reference,
      },
      payment_information: {
        payment_frequency: 'ADHO',
        count_per_period: '1',
        payment_amount_type: 'FIXE',
        amount: {
          currency: 'AUD',
          amount: activeTx.payto.amount,
        },
      },
      additional_information: 'One-off payment for donation',
    };

    const paymentType = activeTx.paymentType;
    console.log('Print payment type: ', paymentType);
    const isInternational = useNavStore.getState().isInternational;
    if (isInternational) {
      await PaymentService.paytoCreateMandate(payload, true);
    } else {
      await PaymentService.paytoCreateMandate(payload);
    }

    console.log('MANDATE CREATED');

    // trigger any success callback
    if (callback) {
      callback();
    }

    return;
  },
  processPaymentPayto: async (uuid, healthEntity, callback) => {
    set({ isPaymentProcessing: true });
    const activeTx = useActiveTransactionStore.getState().activeTx;
    let paytoRes;
    try {
      const healthEntityCode = activeTx.healthEntityCode || activeTx.formData?.healthEntityCode;
      console.log('Using healthEntityCode:', healthEntityCode);
      console.log(healthEntity);
      paytoRes = await PaymentService.paytoRequestMandate(uuid, healthEntity);
    } catch (e) {
      set({ paymentError: e.message, isPaymentProcessing: false });
      return;
    }
    console.log(paytoRes);

    if (!paytoRes) return set({ paymentError: 'Server failed to process the payment' });

    set({ isPaymentProcessing: false });
    set({ isRatingSubmitted: false });
    // reset payment state
    set({ paymentRes: null, paymentError: null });

    // trigger any success callback
    if (callback) {
      callback();
    }

    return paytoRes;
  },
  // Payer auth
  isPayerAuthenticating: false,
  payerAuthData: null,
  payerAuthError: null,
  initPayerAuthentication: async ({ onSuccess = () => {} }) => {
    set({ isPayerAuthenticating: true });

    const { transientTokenJWT } = get();

    const transientToken = getTransientTokenFromJWT(transientTokenJWT);
    const activeTx = useActiveTransactionStore.getState().activeTx;
    console.log('Print active transaction: ', activeTx);
    const payload = {
      code: activeTx.payerAuthCode,
      transientToken,
      healthEntityCode: activeTx.healthEntityCode,
      paymentType: activeTx.paymentType,
    };

    const paymentType = activeTx.paymentType;
    console.log('Print payment type: ', paymentType);
    let payerAuthData;
    const isInternational = useNavStore.getState().isInternational;
    if (isInternational) {
      payerAuthData = await PaymentService.postSetupPayerAuth(payload, true);
    } else {
      payerAuthData = await PaymentService.postSetupPayerAuth(payload);
    }

    console.log('payerAuthData: ', payerAuthData);
    // handles payer authentication error message
    if (!payerAuthData) {
      set({
        isPayerAuthenticating: false,
        payerAuthError: 'Decline - Invalid account number',
      });

      set({ payerAuthData: null });

      return;
    }

    set({ payerAuthData });
    onSuccess();
  },

  // Payer auth enrolment check
  enrollData: null,
  isPayerAuthIFrameEnabled: false,
  initPayerAuthEnrollmentCheck: async ({ onSuccess }) => {
    const { transientTokenJWT, cardDetails, payerAuthData } = get();

    const amount = parseFloat(cardDetails.amount).toFixed(2);
    const transientToken = getTransientTokenFromJWT(transientTokenJWT);
    const activeTx = useActiveTransactionStore.getState().activeTx;
    const code = activeTx.payerAuthCode;
    const healthEntityCode = activeTx.healthEntityCode;
    const paymentType = activeTx.paymentType;
    const referenceId = payerAuthData.consumerAuthenticationInformation.referenceId;
    // const deviceInformation = getBrowserInfo();

    const payload = {
      transientToken,
      code,
      amount,
      referenceId,
      healthEntityCode,
      paymentType,
      // deviceInformation,
    };
    console.log('Print payment type: ', paymentType);
    let enrollData;
    const isInternational = useNavStore.getState().isInternational;
    if (isInternational) {
      // @ts-ignore device info is not included yet
      enrollData = await PaymentService.postCheckPayerAuthEnrollment(payload, false, true);
    } else {
      // @ts-ignore device info is not included yet
      enrollData = await PaymentService.postCheckPayerAuthEnrollment(payload);
    }

    // handles payer auth enrolment error message
    if (!enrollData)
      return set({
        enrollData: null,
        isPayerAuthenticating: false,
        payerAuthError: 'Failed to check enrolment',
      });

    set({ enrollData, isPayerAuthenticating: false });

    // @ts-ignore
    const status = enrollData.status;

    if (status === payerAuthStatus.success) {
      onSuccess();
    } else if (status === payerAuthStatus.pending) {
      set({ isPayerAuthIFrameEnabled: true });
    } else if (status === payerAuthStatus.failed) {
      const errorMessage = getPayerAuthEnrolmentErrorMessage(enrollData);
      set({ payerAuthError: errorMessage });
    }
  },

  // Validate payer auth
  validatePayerAuthData: null,
  initValidatePayerAuth: async ({ transactionId, onSuccess }) => {
    const enrollData = get().enrollData;
    const paConsumerAuthInformation = enrollData.consumerAuthenticationInformation;

    const activeTx = useActiveTransactionStore.getState().activeTx;

    const payload = {
      transactionId,
      paConsumerAuthInformation,
      healthEntityCode: activeTx.healthEntityCode,
      paymentType: activeTx.paymentType,
      code: activeTx.payerAuthCode || activeTx.merchantReference,
    };

    const paymentType = activeTx.paymentType;
    let validatePayerAuthData;
    console.log('Print payment type: ', paymentType);
    const isInternational = useNavStore.getState().isInternational;
    if (isInternational) {
      validatePayerAuthData = await PaymentService.postValidatePayerAuth(payload, true);
    } else {
      validatePayerAuthData = await PaymentService.postValidatePayerAuth(payload);
    }

    console.debug(`validate-payer-auth`, validatePayerAuthData);

    if (!validatePayerAuthData) {
      set({
        payerAuthError: 'Payer validation service could not process the request',
        isPayerAuthIFrameEnabled: false,
      });
      return;
    }

    const status = validatePayerAuthData.status;

    set({ validatePayerAuthData });

    if (status === payerAuthStatus.success) {
      onSuccess();
    } else if (status === payerAuthStatus.failed) {
      const errorMessage = getValidatePayerAuthErrorMessage(validatePayerAuthData);

      set({ payerAuthError: errorMessage, isPayerAuthIFrameEnabled: false });
    }
  },

  // Generate receipt
  isReceiptLoading: false,
  generateReceiptPDF: async (callback) => {
    set({ isReceiptLoading: true });

    const paymentGateway = useActiveTransactionStore.getState().paymentGateway;

    let testReceiptBuffer;

    DebugConsole.log('PAYMENT RES: ', get().paymentRes);
    DebugConsole.log(`Payment gateway: ${paymentGateway}`);

    if (!get().paymentRes) {
      const test = {
        invoice: {
          invoiceReference: '518811560234',
          amount: '25.00',
          healthEntity: 'HNELHD',
          healthEntityDisplay: 'Hunter New England Local Health District',
          hospitalFacilityDisplay: 'All - Centralised Services',
          receivableActivityDisplay: 'Palliative Care',
          fullName: '',
          email: '',
          phone: '',
          title: '',
          organisation: '',
          country: 'AU',
          state: 'New South Wales',
          hospital: 'All - Centralised Services',
          city: 'Hunter Region Mc',
          postalCode: '2310',
          address1: 'Locked Bag 1',
          healthEntityABN: '63598010203',
          maskedValueCC: 'XXXXXXXXXXXX1005',
          cardType: 'Mastercard',
        },
        paymentResponse: {
          id: '7518885928866818803814',
          transactionId: '0707MCC608952',
          submitTimeUtc: '2025-07-07T11:43:13Z',
          authorizedAmount: '25.00',
          status: 'AUTHORIZED',
          retrievalReferenceNumber: '518811560234',
        },
        paymentType: 'donation',
        paymentMethod: 'creditCard',
      };

      testReceiptBuffer = await postGenerateReceipt(test);

      set({ isReceiptLoading: false });

      if (testReceiptBuffer) return callback(testReceiptBuffer);

      return;
    }

    const { paymentData, invoiceData } = get().paymentRes;
    const { paymentMethod, paymentType, ...invoiceFields } = invoiceData;

    let body = {
      invoice: {
        ...invoiceFields,
      },
      paymentResponse: {
        id: '',
        transactionId: '',
        submitTimeUtc: '',
        authorizedAmount: '',
        status: '',
        retrievalReferenceNumber: '',
      },
      paymentType: paymentType,
      paymentMethod: paymentMethod,
    };

    if (paymentGateway === 'bpoint') {
      const txn = paymentData.bpoint;

      body = {
        ...body,
        invoice: {
          ...body.invoice,
          maskedValueCC: txn.paymentMethod.card.number,
          cardType: txn.paymentMethod.card.scheme,
        },
        paymentResponse: {
          id: txn.receiptNumber,
          transactionId: txn.txnNumber,
          submitTimeUtc: txn.processedDateTime,
          authorizedAmount: (txn.amount / 100).toFixed(2),
          status: txn.responseText,
          retrievalReferenceNumber: txn.rrn,
        },
      };
    } else if (paymentGateway === 'cybersource' || !paymentGateway) {
      body = {
        ...body,
        invoice: {
          ...body.invoice,
          maskedValueCC: get().maskedValueCC || '',
          cardType: get().cardType || '',
        },
        paymentResponse: {
          id: paymentData.id,
          transactionId: paymentData.processorInformation.transactionId,
          submitTimeUtc: paymentData.submitTimeUtc.toLocaleString(),
          authorizedAmount: paymentData.orderInformation.amountDetails.authorizedAmount,
          status: paymentData.status,
          retrievalReferenceNumber: paymentData.processorInformation.retrievalReferenceNumber,
        },
      };
    }

    let receiptBuffer;

    console.log('Print payment type: ', paymentType);

    const isInternational = useNavStore.getState().isInternational;

    if (isInternational) {
      receiptBuffer = await postGenerateReceipt(body, true);
    } else {
      receiptBuffer = await postGenerateReceipt(body);
    }

    set({ isReceiptLoading: false });
    if (receiptBuffer) return callback(receiptBuffer);
  },

  generateReprintReceiptPDF: async (param, callback) => {
    set({ isReceiptLoading: true });
    let receiptBuffer;
    const isInternational = useNavStore.getState().isInternational;
    if (isInternational) {
      receiptBuffer = await postGenerateReprintReceipt(param, true);
    } else {
      receiptBuffer = await postGenerateReprintReceipt(param);
    }

    set({ isReceiptLoading: false });
    if (receiptBuffer) return callback(receiptBuffer);
  },

  //Rating
  isRatingSubmitLoading: false,
  isRatingSubmitted: false,
  submitRating: async (callback, rating) => {
    set({ isRatingSubmitLoading: true });
    set({ openRatingForm: false });

    const { paymentData, invoiceData } = get().paymentRes;
    const { paymentMethod, paymentType, ...invoiceFields } = invoiceData;
    let response;
    console.log('Print payment type: ', paymentType);

    const isInternational = useNavStore.getState().isInternational;

    const paymentGateway = useActiveTransactionStore.getState().paymentGateway;

    let rrn = '';

    if (paymentGateway === 'bpoint') {
      rrn = paymentData.bpoint.rrn;
    } else if (paymentGateway === 'cybersource') {
      rrn = paymentData.processorInformation.retrievalReferenceNumber;
    }

    if (isInternational) {
      response = await sendRating(
        {
          rating: rating.rating,
          comment: rating.comment.trim(),
          retrievalReferenceNumber: rrn,
          paymentType: paymentType,
        },
        true,
      );
    } else {
      response = await sendRating({
        rating: rating.rating,
        comment: rating.comment.trim(),
        retrievalReferenceNumber: rrn,
        paymentType: paymentType,
      });
    }

    set({ isRatingSubmitLoading: false });

    if (response) {
      set({ isRatingSubmitted: true });
      return callback(response);
    }
  },

  // Email receipt
  isEmailLoading: false,
  emailReceipt: async (callback, targetEmail) => {
    set({ isEmailLoading: true });

    const paymentGateway = useActiveTransactionStore.getState().paymentGateway;

    let testReceiptBuffer;

    if (!get().paymentRes) {
      const test = {
        invoice: {
          invoiceReference: '518811560234',
          amount: '25.00',
          healthEntity: 'HNELHD',
          healthEntityDisplay: 'Hunter New England Local Health District',
          hospitalFacilityDisplay: 'All - Centralised Services',
          receivableActivityDisplay: 'Palliative Care',
          fullName: '',
          email: '',
          phone: '',
          title: '',
          organisation: '',
          country: 'AU',
          state: 'New South Wales',
          hospital: 'All - Centralised Services',
          city: 'Hunter Region Mc',
          postalCode: '2310',
          address1: 'Locked Bag 1',
          healthEntityABN: '63598010203',
          maskedValueCC: 'XXXXXXXXXXXX1005',
          cardType: 'Mastercard',
        },
        paymentResponse: {
          id: '7518885928866818803814',
          transactionId: '0707MCC608952',
          submitTimeUtc: '2025-07-07T11:43:13Z',
          authorizedAmount: '25.00',
          status: 'AUTHORIZED',
          retrievalReferenceNumber: '518811560234',
        },
        targetEmail: targetEmail,
        paymentType: 'donation',
        paymentMethod: 'creditCard',
      };

      testReceiptBuffer = await sendReceiptEmail(test);

      set({ isReceiptLoading: false });

      if (testReceiptBuffer) return callback(testReceiptBuffer);

      return;
    }

    const { paymentData, invoiceData } = get().paymentRes;
    const { paymentMethod, paymentType, ...invoiceFields } = invoiceData;

    let body = {
      invoice: {
        ...invoiceFields,
      },
      paymentResponse: {
        id: '',
        transactionId: '',
        submitTimeUtc: '',
        authorizedAmount: '',
        status: '',
        retrievalReferenceNumber: '',
      },
      paymentType: paymentType,
      paymentMethod: paymentMethod,
      targetEmail,
    };

    if (paymentGateway === 'bpoint') {
      const txn = paymentData.bpoint;

      body = {
        ...body,
        invoice: {
          ...body.invoice,
          maskedValueCC: txn.paymentMethod.card.number,
          cardType: txn.paymentMethod.card.scheme,
        },
        paymentResponse: {
          id: txn.receiptNumber,
          transactionId: txn.txnNumber,
          submitTimeUtc: txn.processedDateTime,
          authorizedAmount: (txn.amount / 100).toFixed(2),
          status: txn.responseText,
          retrievalReferenceNumber: txn.rrn,
        },
      };
    } else if (paymentGateway === 'cybersource' || !paymentGateway) {
      body = {
        ...body,
        invoice: {
          ...body.invoice,
          maskedValueCC: get().maskedValueCC || '',
          cardType: get().cardType || '',
        },
        paymentResponse: {
          id: paymentData.id,
          transactionId: paymentData.processorInformation.transactionId,
          submitTimeUtc: paymentData.submitTimeUtc.toLocaleString(),
          authorizedAmount: paymentData.orderInformation.amountDetails.authorizedAmount,
          status: paymentData.status,
          retrievalReferenceNumber: paymentData.processorInformation.retrievalReferenceNumber,
        },
      };
    }

    let response;

    console.log('Print payment type: ', paymentType);

    const isInternational = useNavStore.getState().isInternational;

    if (isInternational) {
      response = await sendReceiptEmail(body, true);
    } else {
      response = await sendReceiptEmail(body);
    }

    set({ isEmailLoading: false });

    if (response) return callback(response);
  },

  emailReprintReceipt: async (param, callback, targetEmail) => {
    set({ isEmailLoading: true });
    const body = {
      param,
      targetEmail: targetEmail,
    };
    console.log(body);
    let response;
    const isInternational = useNavStore.getState().isInternational;
    if (isInternational) {
      response = await sendReprintReceiptEmail(body, true);
    } else {
      response = await sendReprintReceiptEmail(body);
    }

    set({ isEmailLoading: false });
    if (response) return callback(response);
  },

  resetValidatePayerAuthData: () => {
    set({ validatePayerAuthData: null });
  },

  resetPaymentStore: () => {
    set({
      // Card details
      maskedValueCC: '',
      cardType: '',
      cardDetails: {},

      captureContext: {},

      // Transient token
      transientTokenJWT: '',
      transientTokenError: {},

      // Process payment
      isPaymentProcessing: false,
      paymentRes: null,
      paymentError: null,
      showPaymentFormErrors: false,

      // Payer auth
      isPayerAuthenticating: false,
      payerAuthData: null,
      payerAuthError: null,

      // Payer auth enrolment check
      enrollData: null,
      isPayerAuthIFrameEnabled: false,

      // Validate payer auth
      validatePayerAuthData: null,

      // Generate receipt
      isReceiptLoading: false,

      // Email receipt
      isEmailLoading: false,

      // reCaptcha
      recaptchaToken: '',
    });
  },

  resetPaymentStoreOnClose: () => {
    useActiveTransactionStore.getState().resetTransaction();
    get().resetPaymentStore();
  },

  validateTerminalPayment: async (
    terminalID,
    terminalSequence,
    terminalReceiptDateTimeFrom,
    isBulk,
    paymentType,
  ) => {
    const allowPayment = await validateTerminalPayment({
      terminalID,
      terminalSequence,
      terminalReceiptDateTimeFrom,
      isBulk,
      paymentType,
    });
    console.log(allowPayment);
    return allowPayment;
  },
}));

// export const useInvoicePaymentStore = create((set, get) => ({
//   activeStep: 'step1',
//   setActiveStep: (step) => set({ activeStep: step }),

//   handleInvoicePaymentSuccess: async () => {
//     usePaymentStore.getState().resetValidatePayerAuthData();
//   },
// }));
